import { filter, reduceBy, view } from "ramda";
import { createSelector } from "reselect";
import {
  spreadBalanceSheetTopLevelChildren,
  spreadBalanceSheetTopLevelChildrenSecondPeriod,
  spreadCashFlowTopLevelChildren,
  spreadCashFlowTopLevelChildrenSecondPeriod,
  spreadIncomeStatementTopLevelChildren,
  spreadIncomeStatementTopLevelChildrenSecondPeriod
} from "../derivedModel/lenses";
import { getSpread } from "../spread/selector";
import {
  SpreadAccount,
  SpreadAccountRow,
  SpreadUnmappedAccount
} from "../spread/types";

export const getUnmappedAccounts = createSelector(
  getSpread,
  spread =>
    spread
      ? filter(
          (unmappedAccount: SpreadUnmappedAccount) =>
            unmappedAccount.period === spread.financialTemplate.periods[0] &&
            unmappedAccount.accountRow != null,
          spread.unmappedAccounts
        )
      : []
);

export const indexUnmappedAccountsByPage = reduceBy(
  (acc: SpreadUnmappedAccount[], unmappedAccount: SpreadUnmappedAccount) =>
    acc.concat([unmappedAccount]),
  [],
  (unmappedAccount: SpreadUnmappedAccount) =>
    unmappedAccount.accountRow.pageNumber.toString()
);

export const getUnmappedAccountsByPage = createSelector(
  getUnmappedAccounts,
  indexUnmappedAccountsByPage
);

export const getMappedAccounts = createSelector(
  getSpread,
  spread => {
    if (spread) {
      const topBalanceSheetChildren = view(spreadBalanceSheetTopLevelChildren)(
        spread
      );
      const topCashFlowChildren = view(spreadCashFlowTopLevelChildren)(spread);
      const topIncomeStatementChildren = view(
        spreadIncomeStatementTopLevelChildren
      )(spread);
      if (spread.financialTemplate.periods.length > 1) {
        const topBalanceSheetChildrenSecondPeriod = view(
          spreadBalanceSheetTopLevelChildrenSecondPeriod
        )(spread);
        const topIncomeStatementChildrenSecondPeriod = view(
          spreadIncomeStatementTopLevelChildrenSecondPeriod
        )(spread);
        const topCashFlowChildrenSecondPeriod = view(
          spreadCashFlowTopLevelChildrenSecondPeriod
        )(spread);
        const mappedBalanceSheetAccountsTwoPeriods = mapMappedAccounts(
          0,
          topBalanceSheetChildren,
          topBalanceSheetChildrenSecondPeriod
        );
        const mappedIncomeStatementAccountsTwoPeriods = mapMappedAccounts(
          1,
          topIncomeStatementChildren,
          topIncomeStatementChildrenSecondPeriod
        );
        const ISAndBSAccountsTwoPeriods = [
          ...mappedBalanceSheetAccountsTwoPeriods,
          ...mappedIncomeStatementAccountsTwoPeriods
        ];
        return mapMappedAccountsHelper(
          2,
          ISAndBSAccountsTwoPeriods,
          topCashFlowChildren,
          topCashFlowChildrenSecondPeriod,
          null,
          null
        );
      }
      const mappedBalanceSheetAccounts = mapMappedAccounts(
        0,
        topBalanceSheetChildren
      );

      const mappedIncomeStatementAccounts = mapMappedAccounts(
        1,
        topIncomeStatementChildren
      );
      const ISAndBSAccounts = [
        ...mappedIncomeStatementAccounts,
        ...mappedBalanceSheetAccounts
      ];
      return mapMappedAccountsHelper(
        2,
        ISAndBSAccounts,
        topCashFlowChildren,
        undefined,
        null,
        null
      );
    }
    return [];
  }
);

export const indexMappedAccountsByPage = reduceBy(
  (acc: SpreadAccountRow[], mappedAccount: SpreadAccountRow) =>
    acc.concat([mappedAccount]),
  [],
  (mappedAccount: SpreadAccountRow) =>
    mappedAccount.pageNumber ? mappedAccount.pageNumber.toString() : ""
);

export const getMappedAccountsByPage = createSelector(
  getMappedAccounts,
  indexMappedAccountsByPage
);

export const mapMappedAccounts = (
  statementType: number,
  accounts: SpreadAccount[],
  secondPeriodAccounts?: SpreadAccount[]
): SpreadAccountRow[] => {
  return mapMappedAccountsHelper(
    statementType,
    [],
    accounts,
    secondPeriodAccounts,
    null,
    null
  );
};

const isSummedAccount = (account: SpreadAccount) => {
  for (const child of account.children) {
    if (
      (child.value.hasModelDerivedValue &&
        child.value.modelDerivedValue &&
        child.value.accountRow) ||
      isSummedAccount(child)
    ) {
      return true;
    }
  }
  return false;
};

export const mapMappedAccountsHelper = (
  statementType: number,
  acc: SpreadAccountRow[],
  accounts: SpreadAccount[],
  secondPeriodAccounts: SpreadAccount[] | undefined,
  parentNodeId: string | null,
  parentNodeName: string | null,
  topLevelHiddenAccountId?: string,
  translatedNames?: { [key: string]: string }
): SpreadAccountRow[] => {
  if (accounts) {
    for (let i = 0; i < accounts.length; i++) {
      const account: SpreadAccount = accounts[i];
      const secondPeriodAccount: SpreadAccount | null =
        secondPeriodAccounts && i < secondPeriodAccounts.length
          ? secondPeriodAccounts[i]
          : null;
      if (
        account.type === "ROOT" ||
        account.type === "CALCULATED" ||
        (account.type === "VALUE" &&
          (isSummedAccount(account) ||
            (secondPeriodAccount && isSummedAccount(secondPeriodAccount))))
      ) {
        mapMappedAccountsHelper(
          statementType,
          acc,
          account.children,
          secondPeriodAccount ? secondPeriodAccount.children : undefined,
          account.id,
          account.name
        );
      } else {
        if (
          account.value.hasModelDerivedValue &&
          (account.value.modelDerivedValue ||
            account.value.modelDerivedValue === 0) &&
          account.value.accountRow &&
          parentNodeId !== null &&
          !isSummedAccount(account)
        ) {
          acc.push({
            ...account.value.accountRow,
            parentNodeId,
            topLevelHiddenAccountId: topLevelHiddenAccountId || account.id,
            parentNodeName: parentNodeName || parentNodeId,
            accountId: account.id,
            statementType,
            asReportedLabel: account.value.asReportedLabel
          });
        } else if (
          secondPeriodAccount &&
          secondPeriodAccount.value.hasModelDerivedValue &&
          (secondPeriodAccount.value.modelDerivedValue ||
            secondPeriodAccount.value.modelDerivedValue === 0) &&
          secondPeriodAccount.value.accountRow &&
          parentNodeId !== null &&
          !isSummedAccount(secondPeriodAccount)
        ) {
          acc.push({
            ...secondPeriodAccount.value.accountRow,
            parentNodeId,
            topLevelHiddenAccountId: topLevelHiddenAccountId || account.id,
            parentNodeName: parentNodeName || parentNodeId,
            accountId: secondPeriodAccount.id,
            statementType,
            asReportedLabel: account.value.asReportedLabel
          });
        } else if (account.type === "HIDDEN") {
          mapMappedAccountsHelper(
            statementType,
            acc,
            account.children,
            secondPeriodAccount ? secondPeriodAccount.children : undefined,
            parentNodeId,
            parentNodeName,
            topLevelHiddenAccountId || account.id
          );
        } else {
          mapMappedAccountsHelper(
            statementType,
            acc,
            account.children,
            secondPeriodAccount ? secondPeriodAccount.children : undefined,
            parentNodeId,
            parentNodeName,
            topLevelHiddenAccountId
          );
        }
      }
    }
  }

  return acc;
};



// WEBPACK FOOTER //
// ./src/spread/model/mappedUnmappedAccounts/selector.ts